import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { Provider } from 'react-redux';
import { store } from './store';
import Topbar from "./scenes/global/Topbar";
import Sidebar from "./scenes/global/Sidebar";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import Dashboard from "./scenes/dashboard";
import ContactsList from "./scenes/contacts";
import ContactDetail from "./scenes/contacts/ContactDetail";
import ContactEdit from "./scenes/contacts/ContactEdit";
import ContactCreate from "./scenes/contacts/ContactCreate";
import ContactImport from "./scenes/contacts/ContactImport";
import Chat from "./scenes/chat";
import Segment from "./scenes/segment";
import Campagne from "./scenes/campagne";
import { ForgotPassword, ResetPassword } from "./scenes/PasswordReset";
import PlanificationCampagne from "./scenes/campagne/planification";
import AssistanceCampagne from './scenes/campagne/assistance';
import CampagneSucces from "./scenes/campagne/succes";
import MauticManual from './scenes/campagne/MauticManual';
import LoginPage from "./scenes/auth/LoginPage";
import RegisterPage from "./scenes/auth/RegisterPage";
import LandingPage from "./scenes/landing/LandingPage";
import AuthLayout from "./scenes/auth/AuthLayout";
import NotFound from "./scenes/NotFound";
import AccessDenied from "./scenes/AccessDenied";
import Emails from './scenes/emails';
import EmailEditorPage from './scenes/emails/EmailEditorPage';
import EmailPreviewPage from './scenes/emails/EmailPreviewPage';
import TextMessages from './scenes/text-messages';
import ClientDiscussions from './scenes/client-discussions';
import PushNotifications from './scenes/push';
import ProtectedRoute from "./components/ProtectedRoute";
import AuthGuard from "./components/AuthGuard";
import { getCurrentUser } from "./services/auth";
import Fidelite from "./scenes/fidelite";
import ProfilFidelite from "./scenes/fidelite/ProfilFidelite";
import AbTestResults from './scenes/abtest-results';
import { NotificationProvider } from "./contexts/NotificationContext";
import { BrandProvider } from "./contexts/BrandContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import SubscriptionSettings from "./scenes/preferences/SubscriptionSettings";
import BillingSettings from "./scenes/preferences/BillingSettings";
import UpgradePlan from "./scenes/preferences/UpgradePlan";

// Dummy components for Emails
/*const Emails = () => {
  return <div>Emails</div>;
};*/

function App() {
  const [theme, colorMode] = useMode();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();

  return (
    <Provider store={store}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <BrandProvider>
            <SubscriptionProvider>
              <NotificationProvider>
                <CssBaseline />
                <Routes>
                  {/* Page publique par défaut pour non connectés */}
                  <Route path="/" element={<LandingPage />} />

                  {/* Routes d'authentification : PAS de sidebar/topbar */}
                  <Route element={<AuthLayout />}>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                  </Route>

                  {/* Routes protégées : sidebar/topbar visibles */}
                  <Route
                    path="*"
                    element={
                      <AuthGuard>
                        <div className="app">
                          <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
                          <main className="content" style={{ width: `calc(100% - ${isCollapsed ? '80px' : '280px'})`, marginLeft: isCollapsed ? '80px' : '280px', transition: 'all 0.3s' }}>
                            <Topbar />
                            <Routes>
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                              <Route path="/contacts" element={
                                <ProtectedRoute requiredModule="marketing">
                                  <ContactsList />
                                </ProtectedRoute>
                              } />
                              <Route path="/contacts/new" element={
                                <ProtectedRoute requiredModule="marketing">
                                  <ContactCreate />
                                </ProtectedRoute>
                              } />
                              <Route path="/contacts/import" element={
                                <ProtectedRoute requiredModule="marketing">
                                  <ContactImport />
                                </ProtectedRoute>
                              } />
                              <Route path="/contacts/:id" element={
                                <ProtectedRoute requiredModule="marketing">
                                  <ContactDetail />
                                </ProtectedRoute>
                              } />
                              <Route path="/contacts/:id/edit" element={
                                <ProtectedRoute requiredModule="marketing">
                                  <ContactEdit />
                                </ProtectedRoute>
                              } />
                              <Route path="/dashboard" element={<Dashboard />} />
                              <Route path="/chat" element={<Chat />} />
                              <Route path="/segment" element={<Segment />} />
                              <Route path="/campagne" element={<Campagne />} />
                              <Route path="/fidelite" element={
                                <ProtectedRoute requiredModule="loyalty_program" requiredUserFlag="loyalty_enabled">
                                  <Fidelite />
                                </ProtectedRoute>
                              } />
                              <Route path="/fidelite/profil/:id" element={
                                <ProtectedRoute requiredModule="loyalty_program" requiredUserFlag="loyalty_enabled">
                                  <ProfilFidelite />
                                </ProtectedRoute>
                              } />
                              <Route path="/contacts/ContactDetail" element={<ContactDetail />} />
                              <Route path="/campagne/planification" element={<PlanificationCampagne />} />
                              <Route path="/campagne/assistance" element={<AssistanceCampagne />} />
                              <Route path="/campagne/succes" element={<CampagneSucces />} />
                              <Route path="/campagne/creation-manuelle" element={<MauticManual />} />
                              <Route path="/emails" element={<Emails />} />
                              <Route path="/text-messages" element={<TextMessages />} />
                              <Route path="/client-discussions" element={<ClientDiscussions />} />
                              <Route path="/emails/:id" element={<EmailEditorPage />} />
                              <Route path="/emails/:id/preview" element={<EmailPreviewPage />} />
                              <Route path="/push" element={<PushNotifications />} />
                              <Route path="/abtest-results/:parentEmailId" element={<AbTestResultsWrapper />} />
                              <Route path="/preferences/subscription" element={<SubscriptionSettings />} />
                              <Route path="/preferences/billing" element={<BillingSettings />} />
                              <Route path="/preferences/upgrade" element={<UpgradePlan />} />
                              <Route path="/access-denied" element={<AccessDenied />} />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </main>
                        </div>
                      </AuthGuard>
                    }
                  />
                </Routes>
              </NotificationProvider>
            </SubscriptionProvider>
          </BrandProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </Provider >
  );
}

// Wrapper pour extraire l'id de l'URL et passer à AbTestResults
function AbTestResultsWrapper() {
  const { parentEmailId } = useParams();
  const search = window.location.search;
  const raw = search.includes('raw=1');
  return <AbTestResults parentEmailId={parentEmailId} raw={raw} />;
}

export default App;
